import { useI18nextInstance } from "@squide/i18next";
import { type ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ComparisonInput } from "#shared/comparisonSelection.ts";
import type { PeriodSelection } from "#shared/periodSelection.ts";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { PeriodFilterButton } from "../PeriodFilterButton.tsx";
import { SeriesRowBadge } from "../SeriesRowBadge.tsx";
import { ComparisonFilterChips } from "./ComparisonFilterChips.tsx";

export interface ComparisonSeriesRowProps {
    letter: string;
    swatchClassName: string;
    label: string;
    selection: ComparisonInput;
    onChange: (selection: ComparisonInput) => void;
    trailingContent?: ReactNode;
}

export function ComparisonSeriesRow({ letter, swatchClassName, label, selection, onChange, trailingContent }: ComparisonSeriesRowProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ComparisonSeriesRow", { i18n: i18nextInstance });

    const handlePeriodChange = useCallback(
        (period: PeriodSelection) =>
            onChange({
                ...selection,
                reviewCycleIds: period.reviewCycleIds?.length ? period.reviewCycleIds : undefined,
                reviewHistoryFilter: period.reviewHistoryFilter,
                from: period.from,
                to: period.to
            }),
        [onChange, selection]
    );

    return (
        <div className="flex flex-wrap items-start gap-2">
            <div className="flex min-w-48 flex-1 flex-wrap items-center gap-2">
                <SeriesRowBadge letter={letter} swatchClassName={swatchClassName} />

                <span className="sr-only">{t("seriesLabel", { letter, label })}</span>

                <PeriodFilterButton period={selection} onChange={handlePeriodChange} />

                <ComparisonFilterChips selection={selection} onChange={onChange} />
            </div>

            {trailingContent && <div className="flex flex-wrap items-center gap-1">{trailingContent}</div>}
        </div>
    );
}
